import type { Asset } from "schemas/generated/asset";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { ImageOff } from "lucide-react";
import { convertToReploSizedImageSource } from "replo-utils/lib/assets";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

export const AssetPreview: React.FC<{
  asset: Asset;
  isUploading?: boolean;
  onLoad?: () => void;
  fit?: Parameters<typeof convertToReploSizedImageSource>[0]["fit"];
}> = ({ asset, isUploading, fit = "contain", onLoad }) => {
  const [isBroken, setIsBroken] = React.useState(false);
  const internalClassName = twMerge(
    "rounded-md object-cover w-full",
    isUploading && "blur-xs opacity-50",
  );
  if (isBroken) {
    return <BrokenAssetCard className={internalClassName} />;
  }

  return exhaustiveSwitch(asset)({
    image: (asset) => {
      const assetUrl = convertToReploSizedImageSource({
        imageSource: asset.url,
        fit,
      });
      return (
        <img
          src={assetUrl}
          alt={asset.name}
          onError={() => setIsBroken(true)}
          onLoad={onLoad}
          className={internalClassName}
        />
      );
    },
    video: (asset) => (
      <video
        src={asset.url}
        className={internalClassName}
        onError={() => setIsBroken(true)}
        onLoadedMetadata={onLoad}
        preload="metadata"
      />
    ),
    font: () => null,
  });
};

const BrokenAssetCard: React.FC<{
  className?: string;
}> = ({ className }) => {
  return (
    <div
      className={twMerge(
        "bg-surface text-muted flex flex-col items-center justify-center gap-1.5 rounded-sm text-center text-sm font-medium",
        className,
      )}
    >
      <ImageOff size={24} />
      <span>Preview unavailable</span>
    </div>
  );
};
